/**
 * TravelerSummary — small helpers that render the new traveler-aware
 * payload fields (priceTotal, occupancy, priceVolatileDirection) on
 * chat hotel/flight cards.
 *
 * Kept as named exports in one file so the imports stay tidy and so
 * future tweaks (badge styling, copy) live in a single place.
 */
import React, { useState } from 'react';
import { Users, AlertTriangle, RefreshCw } from 'lucide-react';

/**
 * FlightPriceTotal — shows "€189 pp · €756 total · 4 travelers" next to
 * the existing per-person PriceTag. Renders nothing when priceTotal is
 * absent (solo trips) so single-passenger cards stay clean.
 *
 * Props:
 *   - priceTotal: string  e.g. "€756" or "€700-€900"
 *   - travelers: number
 *   - className: optional override
 */
export function FlightPriceTotal({ priceTotal, travelers, className = '' }) {
  if (!priceTotal || !travelers || travelers < 2) return null;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[11px] font-semibold text-blue-700 bg-blue-50 border border-blue-200 px-1.5 py-0.5 rounded-md ${className}`}
      title={`Total for ${travelers} travelers`}
      data-testid="flight-price-total"
    >
      <Users className="w-3 h-3" />
      <span>{priceTotal} <span className="font-normal text-gray-500">total · {travelers}</span></span>
    </span>
  );
}

/**
 * HotelOccupancyLine — small italicised line ("1 family room · 4 guests")
 * rendered right under the hotel rating row. Skipped when the hotel
 * carries no occupancy / rooms field (older AI responses).
 */
export function HotelOccupancyLine({ occupancy, rooms }) {
  if (!occupancy && !rooms) return null;
  const label = occupancy || (rooms ? `${rooms} room${rooms > 1 ? 's' : ''}` : null);
  if (!label) return null;
  return (
    <div
      className="flex items-center gap-1 text-xs text-gray-600"
      data-testid="hotel-occupancy"
    >
      <Users className="w-3 h-3 flex-shrink-0" />
      <span className="truncate">{label}</span>
    </div>
  );
}

/**
 * PriceRefreshBadge — small "verify live price" CTA shown on cards
 * tagged `priceVolatile:true` or when the user manually requests a
 * refresh. Hits POST /api/pricing/refresh with the origin/destination
 * + displayed price and surfaces the verdict inline ("Live price
 * dropped to €89 · refresh card?").
 *
 * Props:
 *   - origin: IATA string (e.g. "SOF")
 *   - destination: IATA string (e.g. "BCN")
 *   - displayedPrice: number (EUR, the numeric headline shown on the card)
 *   - direction: "low" | "high" | undefined — drives copy + colour
 *   - apiBase: string — REACT_APP_BACKEND_URL
 */
export function PriceRefreshBadge({ origin, destination, displayedPrice, direction, apiBase }) {
  const [state, setState] = useState({ loading: false, result: null, error: null });

  if (!destination) return null;

  const onClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setState({ loading: true, result: null, error: null });
    try {
      const res = await fetch(`${apiBase}/api/pricing/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ origin, destination, displayedPrice }),
      });
      const data = await res.json();
      setState({ loading: false, result: data, error: null });
    } catch (err) {
      setState({ loading: false, result: null, error: 'Refresh failed' });
    }
  };

  // Colour: amber when volatile/refresh-needed; neutral grey when in-line.
  const isWarn = direction === 'low' || direction === 'high' || state.result?.needsRefresh;
  const wrap = isWarn
    ? 'bg-amber-50 border-amber-200 text-amber-800 hover:bg-amber-100'
    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100';

  return (
    <div className="mt-1.5" data-testid="price-refresh-wrap">
      <button
        type="button"
        onClick={onClick}
        disabled={state.loading}
        className={`inline-flex items-center gap-1 text-[10px] font-medium border rounded-full px-2 py-0.5 transition-colors ${wrap}`}
        data-testid="price-refresh-btn"
        aria-label="Verify live price"
      >
        {isWarn ? <AlertTriangle className="w-3 h-3" /> : <RefreshCw className={`w-3 h-3 ${state.loading ? 'animate-spin' : ''}`} />}
        <span>
          {state.loading
            ? 'Checking…'
            : direction === 'high'
            ? 'Price may be elevated · verify live'
            : direction === 'low'
            ? 'Volatile low price · verify live'
            : 'Verify live price'}
        </span>
      </button>
      {state.result?.message && (
        <p
          className="text-[10px] text-gray-600 mt-1 leading-snug"
          data-testid="price-refresh-message"
        >
          {state.result.message}
          {typeof state.result.deltaRatio === 'number' && (
            <span className="ml-1 text-gray-400">
              ({Math.round(state.result.deltaRatio * 100)}% drift)
            </span>
          )}
        </p>
      )}
      {state.error && (
        <p className="text-[10px] text-red-600 mt-1">{state.error}</p>
      )}
    </div>
  );
}
